import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import type { NostrEvent } from '@nostrify/nostrify';

// Search relay optimized for content searches
const SEARCHABLE_RELAY_URLS = [
  'wss://relay.nostr.band/',
  'wss://search.nos.today/',
];

/**
 * Hook to search for text content in notes
 */
export function useContentSearch(query: string, enabled = true) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['content-search', query],
    queryFn: async (c): Promise<NostrEvent[]> => {
      if (!query.trim()) return [];

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(1500)]);

      try {
        const searchGroup = nostr.group(SEARCHABLE_RELAY_URLS);
        const events = await searchGroup.query(
          [
            {
              kinds: [1], // Short text notes
              search: query,
              limit: 20,
            },
          ],
          { signal }
        );

        return events;
      } catch (error) {
        console.error('Content search failed:', error);
        return [];
      }
    },
    enabled: enabled && !!query.trim(),
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // Keep in cache for 5 minutes
    placeholderData: (previousData) => previousData, // Show previous results while loading
  });
}
